/**
 * Empty-state CTA shown inside ConnectorPickerButton's popover when
 * the user has zero ACTIVE connections. Deep-links to Settings so
 * the popover never feels like a dead UI for first-time users.
 *
 * A guest (read-only role) always has zero connections — connections are
 * scoped `(workspaceId, userId)`, not shared — so they would always land on
 * this empty state. The Settings deep-link CTA is a dead end for them:
 * `/settings?tab=connectors` → Connect → `POST /api/connectors/start` is
 * write-gated (`requireWriteAccess`) and 403s. Read-only roles get
 * non-actionable copy instead; the picker itself (and its always-on
 * workspace/OpenAlex/web sources) stays available either way.
 */

import { Button, Link, Text, VStack } from '@chakra-ui/react';
import { useIsReadOnly } from '../../../hooks/user/useWorkspaceRole';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { HELP_LINKS } from '../../../constants/helpLinks';

interface EmptyConnectorsPromptProps {
  readonly onOpenSettings: () => void;
}

export function EmptyConnectorsPrompt({ onOpenSettings }: EmptyConnectorsPromptProps) {
  const isReadOnly = useIsReadOnly();

  return (
    // Left-aligned, like every other block of prose in the product. Centred
    // copy was inherited from reading this as an empty-state illustration; it
    // is three lines of explanation with a link in the middle, and centring
    // gives every line a different starting edge for the eye to find.
    <VStack spacing={3} py={3} align="stretch">
      <Text fontSize="xs" color="text.muted">
        Bower already searches your workspace, OpenAlex, Semantic Scholar, and the web.{' '}
        <Link href={HELP_LINKS.CONNECTORS} isExternal>
          learn more
        </Link>
        . Connect tools like Drive, Notion, or Slack to extend that to where the rest of your work
        lives.
      </Text>
      {isReadOnly ? (
        <Text fontSize="xs" color="text.muted">
          Connecting a source needs a workspace member. Ask one to add it in Settings.
        </Text>
      ) : (
        // The product's primary action button, not a bespoke one. This was an
        // `HStack as="button"` that reimplemented a subset of the Button
        // styles, so the one real call to action in this panel read as quieter
        // than "New Collection" or "New note" — the same kind of action, one
        // surface along.
        <Button
          variant="primary"
          size="sm"
          alignSelf="flex-start"
          onClick={onOpenSettings}
          leftIcon={<MaterialIcon name="power" boxSize={4} color="inherit" />}
        >
          Add a connector
        </Button>
      )}
    </VStack>
  );
}
